import React from 'react';
import { ShieldCheck, AlertCircle, Loader2, ExternalLink } from 'lucide-react';
import { CheckoutPricing } from '@/types';
import { COUNTRY_OPTIONS } from '@/constants';
import { formatCardNumber, formatExpiry } from '@/lib/formatters';
import { ThreeDSData } from './bookingFlowTypes';

interface CardDetails {
  number: string;
  expiry: string;
  cvc: string;
  cardholderName: string;
}

interface BillingAddress {
  address1: string;
  city: string;
  postalCode: string;
  country: string;
}

interface PaymentPanelProps {
  requiresCardPayment: boolean;
  cardDetails: CardDetails;
  billingAddress: BillingAddress;
  pricing: CheckoutPricing;
  agreedToTerms: boolean;
  isProcessingPayment: boolean;
  showInlineFormError: boolean;
  formError: string;
  threeDSData: ThreeDSData | null;
  bookingPolicyLink: { title: string; url: string };
  onCardDetailsChange: (details: CardDetails) => void;
  onBillingAddressChange: (address: BillingAddress) => void;
  onAgreedToTermsChange: (agreed: boolean) => void;
  onSubmit: (e: React.FormEvent) => void;
  onThreeDSCancel: () => void;
}

const BOOKING_POLICY_ACCEPTANCE_LABEL =
  'I confirm that I have read and accept the terms and conditions from below:';

export const PaymentPanel: React.FC<PaymentPanelProps> = ({
  requiresCardPayment,
  cardDetails,
  billingAddress,
  pricing,
  agreedToTerms,
  isProcessingPayment,
  showInlineFormError,
  formError,
  threeDSData,
  bookingPolicyLink,
  onCardDetailsChange,
  onBillingAddressChange,
  onAgreedToTermsChange,
  onSubmit,
  onThreeDSCancel
}) => {
  const total = pricing.totalAmount;

  return (
    <form onSubmit={onSubmit} className="space-y-4">
      {requiresCardPayment && (
        <div className="bg-slate-900 rounded-2xl p-6 text-white shadow-xl relative overflow-hidden">
          <div className="relative z-10 mb-4 space-y-2">
            <h4 className="text-xs font-black uppercase tracking-[0.2em] text-teal-400">
              Secure Payment
            </h4>
            <div className="flex items-center gap-1 text-[9px] font-bold text-slate-400 bg-white/5 px-2 py-1 rounded w-fit">
              <ShieldCheck className="w-3 h-3 text-teal-500" />
              SECURED BY OPAYO
            </div>
          </div>
          <div className="relative z-10 space-y-3">
            <input
              className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2 text-white text-sm outline-none focus:border-teal-500"
              placeholder="Card Number"
              maxLength={19}
              value={cardDetails.number}
              onChange={(e) =>
                onCardDetailsChange({ ...cardDetails, number: formatCardNumber(e.target.value) })
              }
            />
            <input
              className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2 text-white text-sm outline-none focus:border-teal-500"
              placeholder="Cardholder Name"
              maxLength={50}
              value={cardDetails.cardholderName}
              onChange={(e) =>
                onCardDetailsChange({ ...cardDetails, cardholderName: e.target.value })
              }
            />
            <div className="grid grid-cols-2 gap-3">
              <input
                className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2 text-white text-sm outline-none focus:border-teal-500"
                placeholder="MM / YY"
                maxLength={5}
                value={cardDetails.expiry}
                onChange={(e) =>
                  onCardDetailsChange({ ...cardDetails, expiry: formatExpiry(e.target.value) })
                }
              />
              <input
                className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2 text-white text-sm outline-none focus:border-teal-500"
                placeholder="CVC"
                maxLength={4}
                value={cardDetails.cvc}
                onChange={(e) =>
                  onCardDetailsChange({
                    ...cardDetails,
                    cvc: e.target.value.replace(/[^0-9]/g, '')
                  })
                }
              />
            </div>
            <input
              className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2 text-white text-sm outline-none focus:border-teal-500"
              placeholder="Billing Address"
              value={billingAddress.address1}
              onChange={(e) =>
                onBillingAddressChange({ ...billingAddress, address1: e.target.value })
              }
            />
            <div className="grid grid-cols-2 gap-3">
              <input
                className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2 text-white text-sm outline-none focus:border-teal-500"
                placeholder="City"
                value={billingAddress.city}
                onChange={(e) =>
                  onBillingAddressChange({ ...billingAddress, city: e.target.value })
                }
              />
              <input
                className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2 text-white text-sm outline-none focus:border-teal-500"
                placeholder="Postcode"
                value={billingAddress.postalCode}
                onChange={(e) =>
                  onBillingAddressChange({ ...billingAddress, postalCode: e.target.value })
                }
              />
            </div>
            <select
              className="w-full bg-slate-900 border border-white/10 rounded-xl px-4 py-2 text-white text-sm outline-none focus:border-teal-500"
              style={{ colorScheme: 'dark' }}
              value={billingAddress.country}
              onChange={(e) =>
                onBillingAddressChange({ ...billingAddress, country: e.target.value })
              }
            >
              {COUNTRY_OPTIONS.map((option) => (
                <option
                  key={option.code}
                  value={option.code}
                  style={{ backgroundColor: '#0f172a', color: '#f8fafc' }}
                >
                  {option.label} ({option.code})
                </option>
              ))}
            </select>
          </div>
          <div className="relative z-10 mt-4 border-t border-white/10 pt-3 space-y-2 text-xs">
            <div className="flex items-center justify-between">
              <span className="text-slate-400 font-medium">Subtotal</span>
              <span className="text-white font-bold">£{pricing.subtotalAmount.toFixed(2)}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-slate-400 font-medium">
                VAT ({pricing.vatRatePercent.toFixed(2)}%)
              </span>
              <span className="text-white font-bold">£{pricing.vatAmount.toFixed(2)}</span>
            </div>
            <div className="flex items-center justify-between border-t border-white/10 pt-2">
              <span className="text-slate-300 font-semibold">Transaction Total</span>
              <span className="text-white text-base font-black">
                £{pricing.totalAmount.toFixed(2)}
              </span>
            </div>
          </div>
          <div className="mt-4 flex items-center justify-center gap-3 grayscale opacity-40">
            <img
              src="https://upload.wikimedia.org/wikipedia/commons/thumb/5/5e/Visa_Inc._logo.svg/2560px-Visa_Inc._logo.svg.png"
              className="h-2 w-auto"
              alt="Visa"
            />
            <img
              src="https://upload.wikimedia.org/wikipedia/commons/thumb/2/2a/Mastercard-logo.svg/1280px-Mastercard-logo.svg.png"
              className="h-4 w-auto"
              alt="Mastercard"
            />
          </div>
        </div>
      )}

      <div className="bg-slate-100 p-4 rounded-xl border border-slate-200">
        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            className="w-5 h-5 text-teal-600 rounded mt-0.5"
            checked={agreedToTerms}
            onChange={(e) => onAgreedToTermsChange(e.target.checked)}
            required
          />
          <div className="text-[10px] text-slate-600 leading-relaxed font-medium">
            <span>{BOOKING_POLICY_ACCEPTANCE_LABEL}</span>{' '}
            <a
              href={bookingPolicyLink.url}
              target="_blank"
              rel="noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="inline-flex items-center gap-1 font-semibold text-teal-700 underline underline-offset-2"
            >
              <span>{bookingPolicyLink.title}</span>
              <ExternalLink className="w-3 h-3" />
            </a>
          </div>
        </label>
      </div>

      {showInlineFormError && (
        <div className="bg-red-50 text-red-600 p-3 rounded-lg text-xs font-bold flex items-center gap-2 border border-red-100">
          <AlertCircle className="w-4 h-4" /> {formError}
        </div>
      )}

      <button
        type="submit"
        disabled={isProcessingPayment || !agreedToTerms}
        className={`w-full text-white py-4 rounded-2xl font-black text-lg transition-all shadow-xl disabled:opacity-50 active:scale-95 uppercase tracking-widest ${isProcessingPayment ? 'bg-slate-700' : 'bg-teal-600 hover:bg-teal-700 shadow-teal-600/20'}`}
      >
        {isProcessingPayment ? (
          <div className="flex items-center justify-center gap-2">
            <Loader2 className="w-5 h-5 animate-spin" /> Authorizing...
          </div>
        ) : requiresCardPayment ? (
          `Pay £${total.toFixed(2)} & Book`
        ) : (
          'Confirm Booking'
        )}
      </button>

      {threeDSData && (
        <div className="fixed inset-0 z-50 bg-slate-900/70 flex items-center justify-center p-4">
          <div className="bg-white w-full max-w-2xl rounded-2xl shadow-2xl p-4">
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-bold text-slate-800">3D Secure Verification</h4>
              <span className="text-xs text-slate-400">Complete the challenge to continue</span>
            </div>
            <iframe
              title="3DS Verification"
              name="opayo-3ds-iframe"
              className="w-full h-[70vh] border rounded-xl"
            />
            <div className="flex items-center justify-between gap-3 mt-3">
              <span className="text-xs text-slate-400">
                Having trouble? If the verification does not load or seems stuck, cancel to check
                your payment status.
              </span>
              <button
                type="button"
                onClick={onThreeDSCancel}
                className="shrink-0 text-sm font-bold text-slate-500 hover:text-slate-700 border border-slate-200 rounded-xl px-4 py-2"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </form>
  );
};
